import { Eye, Pencil } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { IncludedVacanciesSection } from "./IncludedVacanciesSection";
import { RecentActivitySection } from "./RecentActivitySection";
import { WhereUsedSection } from "./WhereUsedSection";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Separator } from "@/components/ui/v2/separator";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { TruncatedTooltip } from "@/components/ui/v2/truncated-tooltip";
import { useSegmentActivity } from "@/features/segments/api/useSegmentActivity";
import { useSegmentVacancyPreview } from "@/features/segments/api/useSegmentVacancyPreview";
import { useSegmentPaths } from "@/features/segments/builder/hooks/useSegmentPaths";
import { ArchiveSegmentAction } from "@/features/segments/components/ArchiveSegmentAction";
import { DuplicateSegmentAction } from "@/features/segments/components/DuplicateSegmentAction";
import { SEGMENT_STATUS_CONFIG } from "@/features/segments/constants";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import type { SegmentDetailResponse } from "@/features/segments/types";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";

type SegmentDetailProps = {
  segment: SegmentDetailResponse;
};

function SectionSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
    </div>
  );
}

export function SegmentDetail({ segment }: SegmentDetailProps) {
  const { t } = useTranslation();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const paths = useSegmentPaths();
  const { canCreate, canEdit } = usePermissionMatrix();
  const openRef = useTourTarget<HTMLAnchorElement>(SEGMENT_TOUR_TARGET.OVERVIEW_OPEN);
  const archiveRef = useTourTarget<HTMLDivElement>(SEGMENT_TOUR_TARGET.OVERVIEW_ARCHIVE);
  const { labelKey, variant } = SEGMENT_STATUS_CONFIG[segment.status];
  const { data: vacancies, isLoading: vacanciesLoading } =
    useSegmentVacancyPreview({
      segmentId: segment.id,
    });
  const { data: activities, isLoading: activitiesLoading } = useSegmentActivity(
    {
      segmentId: segment.id,
      clientId,
    },
  );

  return (
    <motion.div
      key={segment.id}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="flex flex-col"
    >
      <div className="flex items-start justify-between gap-3 pb-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <TruncatedTooltip text={segment.name}>
              <h2 className="truncate text-lg font-semibold">{segment.name}</h2>
            </TruncatedTooltip>
            <Badge variant={variant} size="small">
              {t(labelKey)}
            </Badge>
          </div>
        </div>
        {canEdit("segments") ? (
          <div className="flex shrink-0 items-center gap-2">
            {canCreate("segments") && (
              <DuplicateSegmentAction segment={segment} clientId={clientId} variant="icon" />
            )}
            <div ref={archiveRef}>
              <ArchiveSegmentAction
                segment={segment}
                clientId={clientId}
                variant="icon"
                onArchived={() => navigate(paths.overview, { replace: true })}
              />
            </div>
            <Button variant="default" size="sm" asChild>
              <Link ref={openRef} to={`${segment.id}/edit`}>
                <Pencil className="mr-1.5 size-3.5" />
                {t("common.editSegment", "Edit Segment")}
              </Link>
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" asChild>
            <Link ref={openRef} to={`${segment.id}`}>
              <Eye className="mr-1.5 size-3.5" />
              {t("common.viewSegment", "View Segment")}
            </Link>
          </Button>
        )}
      </div>

      <div className="mt-6 flex flex-col">
        <div className="pb-6">
          <WhereUsedSection
            campaigns={segment.campaigns}
            modules={segment.modules}
          />
        </div>

        <Separator className="mb-6" />

        <div className="pb-6">
          {vacanciesLoading && <SectionSkeleton />}
          {!vacanciesLoading && vacancies && (
            <IncludedVacanciesSection vacancies={vacancies} />
          )}
        </div>

        <Separator className="mb-6" />

        <div>
          {activitiesLoading && <SectionSkeleton />}
          {!activitiesLoading && activities && (
            <RecentActivitySection activities={activities} />
          )}
        </div>
      </div>
    </motion.div>
  );
}

export function SegmentDetailSkeleton() {
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-8 w-28" />
      </div>
      <div className="mt-6 flex flex-col gap-6">
        <SectionSkeleton />
        <Separator />
        <SectionSkeleton />
        <Separator />
        <SectionSkeleton />
      </div>
    </div>
  );
}
